import { $isLinkNode } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { $isMarkNode } from '@lexical/mark';
import { $isHeadingNode, HeadingNode } from '@lexical/rich-text';
import {
  $createRangeSelection,
  $createTextNode,
  $isTextNode,
  ElementNode,
  LexicalNode,
  RangeSelection,
  $isParagraphNode,
  ParagraphNode,
  TextNode,
} from 'lexical';
import { partition } from 'lodash';
import { $getAncestor, $getDirectChild } from '../../lexicalUtils';
import { $isCustomListItemNode, CustomListItemNode } from '../CustomListItemNode';
import { $createNoWrapNode, $isNoWrapNode, NoWrapNode } from './NoWrapNode';

export * from './removeEmptyNoWrapNodesByKeys';

/**
 * Return the **outermost** LinkNode or MarkNode ancestor (furthest from the leaf).
 * If none found – returns null.
 */
function getOutermostWrapper(node: LexicalNode | null): LexicalNode | null {
  let outer: LexicalNode | null = null;
  let current = node;
  while (current) {
    if ($isLinkNode(current) || $isMarkNode(current)) {
      outer = current;
    }
    current = current.getParent();
  }

  return outer;
}

/** Word boundary helpers */
const WS = /\s/;

const wordStart = (text: string, offset: number): number => {
  while (offset > 0 && !WS.test(text[offset - 1])) {
    offset--;
  }

  return offset;
};

const wordEnd = (text: string, offset: number): number => {
  while (offset < text.length && !WS.test(text[offset])) {
    offset++;
  }

  return offset;
};

function $normalizeSelection(selection: RangeSelection): RangeSelection {
  // A new selection is used to avoid side-effects of flipping the given selection
  const forwardSelection = $createRangeSelection();
  const [startPoint, endPoint] = selection.isBackward()
    ? [selection.focus, selection.anchor]
    : [selection.anchor, selection.focus];

  forwardSelection.anchor.set(
    startPoint.key,
    startPoint.offset,
    startPoint.type,
  );

  forwardSelection.focus.set(
    endPoint.key,
    endPoint.offset,
    endPoint.type);

  const anchorNode = forwardSelection.anchor.getNode();
  const focusNode = forwardSelection.focus.getNode();

  // Detect outermost mark/link for both ends
  const anchorOut = getOutermostWrapper(anchorNode);
  const focusOut = getOutermostWrapper(focusNode);

  let anchorKey = forwardSelection.anchor.key;
  let anchorOffset = forwardSelection.anchor.offset;
  let anchorType: 'text' | 'element' = $isTextNode(anchorNode) ? 'text' : 'element';

  if (anchorOut) {
    anchorKey = anchorOut.getKey();
    anchorOffset = 0; // start of element
    anchorType = 'element';
  } else if ($isTextNode(anchorNode)) {
    anchorOffset = wordStart(anchorNode.getTextContent(), forwardSelection.anchor.offset);
  }

  let focusKey = forwardSelection.focus.key;
  let focusOffset = forwardSelection.focus.offset;
  let focusType: 'text' | 'element' = $isTextNode(focusNode) ? 'text' : 'element';

  if (focusOut) {
    focusKey = focusOut.getKey();
    focusOffset = (focusOut as ElementNode).getChildrenSize(); // after last child
    focusType = 'element';
  } else if ($isTextNode(focusNode)) {
    focusOffset = wordEnd(focusNode.getTextContent(), forwardSelection.focus.offset);
  }

  forwardSelection.anchor.set(anchorKey, anchorOffset, anchorType);
  forwardSelection.focus.set(focusKey, focusOffset, focusType);

  return forwardSelection;
}

function $removeNoWrapNode(node: NoWrapNode): void {
  const children = node.getChildren();
  let target: LexicalNode | null = null;

  for (const child of children) {
    if (target === null) {
      node.insertBefore(child);
    } else {
      target.insertAfter(child);
    }
    target = child;
  }
  node.remove();
}

function $isNodeWithTextContent(node: LexicalNode): boolean {
  return node.getTextContent().trim().length !== 0;
}

export function hasLegacyNoWrapStyle(style: string): boolean {
  return /(^|;)\s*white-space\s*:\s*nowrap\s*(;|$)/i.test(style);
}

export function $isNoWrapTextNode(node: TextNode): boolean {
  return Boolean($getAncestor(node, $isNoWrapNode) ?? $getDirectChild(node, $isNoWrapNode))
    || hasLegacyNoWrapStyle(node.getStyle());
}

function $moveLegacyNoWrapContent(node: TextNode): void {
  const text = node.getTextContent();
  const prevSibling = node.getPreviousSibling();
  const nextSibling = node.getNextSibling();

  if (prevSibling && $isTextNode(prevSibling)) {
    prevSibling.setTextContent(prevSibling.getTextContent() + text);
    node.remove();

    return;
  }

  if (nextSibling && $isTextNode(nextSibling)) {
    nextSibling.setTextContent(text + nextSibling.getTextContent());
    node.remove();

    return;
  }

  // No text neighbours — replace with a new clean text node
  const newNode = $createTextNode(text);
  node.insertBefore(newNode);
  node.remove();
}

/** Remove any `NoWrapNode` that currently contains the given nodes. */
export function $unwrapNodes(selection: RangeSelection): boolean {
  const nodes = selection.getNodes().filter($isNodeWithTextContent);
  const allWrapped = nodes.every(node => ($getAncestor(node, $isNoWrapNode) ?? $getDirectChild(node, $isNoWrapNode)));

  for (const node of nodes) {
    const parent = node.getParent();
    if ($isNoWrapNode(parent)) {
      $removeNoWrapNode(parent);
    }
    if ($isTextNode(node) && hasLegacyNoWrapStyle(node.getStyle())) {
      $moveLegacyNoWrapContent(node);
    }
  }

  return allWrapped;
}

const $isBlockNode = (node: LexicalNode): node is ParagraphNode | HeadingNode | ListNode => {
  return $isParagraphNode(node) || $isHeadingNode(node) || $isListNode(node);
};

/** Wrap children of an ElementNode inside a new NoWrapNode. */
function $wrapChildrenInNoWrap(parent: ElementNode): void {
  const children = parent.getChildren();
  if (!children.length) {
    return;
  }
  const wrapper = $createNoWrapNode();
  children[0].insertBefore(wrapper);
  children.forEach(child => wrapper.append(child));
}

/** Recursively wrap inline content of every ListItemNode inside a ListNode. */
function $wrapListNoWrap(list: ListNode): void {
  list.getChildren().forEach((listItem) => {
    if ($isCustomListItemNode(listItem)) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
      $wrapListItemInlineChildren(listItem);
    }
  });
}

/** Wrap inline children of a ListItemNode, skipping nested ListNodes.
 *  Recurses into nested lists so all levels are wrapped. */
function $wrapListItemInlineChildren(listItem: CustomListItemNode): void {
  const children = listItem.getChildren();

  for (const child of children) {
    if ($isListNode(child)) {
      $wrapListNoWrap(child);
    }
  }

  const inlineChildren = children.filter(child => !$isListNode(child));
  if (!inlineChildren.length) {
    return;
  }
  const wrapper = $createNoWrapNode();
  inlineChildren[0].insertBefore(wrapper);
  inlineChildren.forEach(child => wrapper.append(child));
}

/** Wrap the current selection in a new NoWrapNode */
export function $wrapNodes(selection: RangeSelection): void {
  const forwardSelection = $normalizeSelection(selection);
  const nodes = forwardSelection.extract().filter($isNodeWithTextContent);

  const topLevelNodes = nodes.filter((node) => {
    const parent = node.getParent();

    return !parent || !nodes.find(n => n.getKey() === parent.getKey());
  });

  const [customListItemNodes, otherTopLevelNodes] = partition(topLevelNodes, node => $isCustomListItemNode(node));

  if (customListItemNodes.length) {
    customListItemNodes.forEach((node: CustomListItemNode) => {
      $wrapListItemInlineChildren(node);
    });
  }

  if (!otherTopLevelNodes.length) {
    return;
  }

  const [blockNodes, inlineNodes] = partition(otherTopLevelNodes, $isBlockNode);

  if (inlineNodes.length) {
    const inlineWrapper = $createNoWrapNode();
    inlineNodes[0].insertBefore(inlineWrapper);
    inlineNodes.forEach(node => inlineWrapper.append(node));
  }

  blockNodes.forEach((blockNode) => {
    if ($isListNode(blockNode)) {
      $wrapListNoWrap(blockNode);

      return;
    }

    $wrapChildrenInNoWrap(blockNode);
  });

}

